import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setProjects([]);
      setLoading(false);
      return;
    }

    // Create query for user's projects
    const q = query(
      collection(db, 'projects'),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const projectsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProjects(projectsData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error fetching projects:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Create a new project
  const createProject = async (projectData) => {
    try {
      if (!currentUser) throw new Error('User not authenticated');

      const docRef = await addDoc(collection(db, 'projects'), {
        ...projectData,
        userId: currentUser.uid,
        documentCount: 0,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { success: true, id: docRef.id };
    } catch (err) {
      console.error('Error creating project:', err);
      return { success: false, error: err.message };
    }
  };

  // Update a project
  const updateProject = async (projectId, updates) => {
    try {
      if (!currentUser) throw new Error('User not authenticated');

      const projectRef = doc(db, 'projects', projectId);
      await updateDoc(projectRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (err) {
      console.error('Error updating project:', err);
      return { success: false, error: err.message };
    }
  };

  // Delete a project
  const deleteProject = async (projectId) => {
    try {
      if (!currentUser) throw new Error('User not authenticated');

      const projectRef = doc(db, 'projects', projectId);
      await deleteDoc(projectRef);

      return { success: true };
    } catch (err) {
      console.error('Error deleting project:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    projects,
    loading,
    error,
    createProject,
    updateProject,
    deleteProject,
  };
}
